<template lang="pug">
.details
  .section-title {{ data?.label }}
  .details-fields(v-if="scheme.length > 0")
    .details-row(v-for="(row, index) in scheme", :key="`row-${index}`")
      .details-field(v-for="field in row", :key="field.name", :style="{ width: `${field.width}%` }")
        .field-label {{ getField(field.name)?.label }}
        q-skeleton.field-value(v-if="fieldsLoading", animation="blink", :dark="theme === 'dark'")
        .field-value(v-else) {{ getFieldValue(field.name) }}
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { handleError } from "@/services/handleErrors";
import { format } from "date-fns";
import { theme } from "@/services/useTheme";
import backend from "@/api";

const props = defineProps({
  /** Дата текущей секции. Пример: 
  {
    "type": "table",
    "data_url": "/acts/24",
    "components": {
      "fields": [
          {
              "label": "Внешний ID",
              "type": "string",
              "name": "external_id"
          },
          {
              "label": "Статус",
              "type": "string",
              "name": "status"
          },
          {
              "label": "Компания исполнителя",
              "type": "string",
              "name": "executor_company"
          },
          {
              "label": "Договор",
              "type": "string",
              "name": "contact_title"
          },
          {
              "label": "Тип заявки",
              "type": "string",
              "name": "issue_type"
          },
          {
              "label": "ID заявки",
              "type": "string",
              "name": "issue_id"
          },
          {
              "label": "Внешний ID заявки",
              "type": "string",
              "name": "issue_external_id"
          },
          {
              "label": "Дата создания",
              "type": "datetime",
              "format": "%H:%M %d.%m.%Y",
              "name": "created_at"
          },
          {
              "label": "Группа системы",
              "type": "string",
              "name": "group_system_title"
          },
          {
              "label": "Система",
              "type": "string",
              "name": "system_title"
          },
          {
              "label": "Оборудование",
              "type": "string",
              "name": "equipment_title"
          },
          {
              "label": "Общая стоимость",
              "type": "currency",
              "name": "total_cost"
          }
      ],
      "scheme": [
          [
              {
                  "name": "external_id",
                  "width": 33.3
              },
              {
                  "name": "status",
                  "width": 33.3
              },
              {
                  "name": "executor_company",
                  "width": 33.3
              }
          ],
          [
              {
                  "name": "contact_title",
                  "width": 50
              },
              {
                  "name": "issue_type",
                  "width": 50
              }
          ],
          [
              {
                  "name": "issue_id",
                  "width": 50
              },
              {
                  "name": "issue_external_id",
                  "width": 50
              }
          ],
          [
              {
                  "name": "created_at",
                  "width": 50
              },
              {
                  "name": "group_system_title",
                  "width": 50
              }
          ],
          [
              {
                  "name": "system_title",
                  "width": 50
              },
              {
                  "name": "equipment_title",
                  "width": 50
              }
          ],
          [
              {
                  "name": "total_cost",
                  "width": 100
              }
          ]
      ]
    }
  }
  */
  data: { type: Object, default: () => {} },
});

const fieldsData = ref({});
const fieldsLoading = ref(true);

const data = computed(() => props.data);
const fields = computed(() => data.value.components?.fields ?? []);
const scheme = computed(() => data.value.components?.scheme ?? []);
const path = computed(() => `/api/v3/${data.value.data_url}`);

const getField = name => {
  return fields.value.find(item => item.name === name);
};

const getFieldValue = name => {
  const field = getField(name);
  const val = fieldsData.value[name];
  const date = new Date(val);

  if (!val) return "";

  if (field.type === "datetime") {
    return format(date, "dd.MM.yyyy, HH:mm");
  } else if (field.type === "date") {
    return format(date, "dd.MM.yyyy");
  }

  return val;
};

const getDetailsData = async () => {
  try {
    const { data } = await backend.index(path.value, {});
    fieldsData.value = data;
  } catch (err) {
    await handleError(err);
  } finally {
    fieldsLoading.value = false;
  }
};

onMounted(async () => {
  await getDetailsData();
});
</script>

<style scoped lang="scss">
.details {
  .section-title {
    font-size: 16px;
    margin-top: 16px;
    font-weight: 600;
    color: var(--header-title-color);
  }

  .details-fields {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    color: var(--show-card-info-block-color);

    .details-row {
      display: flex;
      gap: 16px;
    }

    .field-label {
      font-weight: 600;
      font-size: 14px;
    }

    .field-value {
      margin-top: 8px;
      display: block;
      border: var(--show-card-info-block-border);
      padding: 10px;
      border-radius: 8px;
      word-break: break-word;
      min-height: 43px;
    }
  }
}
</style>
